import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import ShareIcon from '@mui/icons-material/Share';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { shortenUrl } from '../utils/urlShortener';
import { useAuth } from '../hooks/useAuth';

export default function ShareFileDialog({ url, setOpen, open, onGenerateLink, isGenerating }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { jwt } = useAuth(); // Use Strapi JWT
    const [expirationHours, setExpirationHours] = useState(168); // 7 days default
    const [copied, setCopied] = useState(false);
    const [shareUrl, setShareUrl] = useState(null);
    const [isShortening, setIsShortening] = useState(false);

    const handleCopyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            enqueueSnackbar(t('LinkCopiedToClipboard') || t("LinkSuccess"), { variant: 'success' });
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            enqueueSnackbar(t('FailedToCopyLink') || 'Failed to copy link', { variant: 'error' });
        }
    };

    const handleGenerateLink = async () => {
        if (onGenerateLink) {
            onGenerateLink(expirationHours);
        } else {
            // Create a shortened URL for the existing URL
            setIsShortening(true);
            try {
                const result = await shortenUrl(url, jwt, expirationHours);
                setShareUrl(result.shortUrl);
                
                if (result.method === 'fallback') {
                    enqueueSnackbar(t('UsingLocalFallback') || 'Using local storage fallback - link may not work on other devices', { variant: 'warning' });
                } else if (!result.success) {
                    enqueueSnackbar(t('ShorteningFailed') || 'URL shortening failed, using original URL', { variant: 'warning' });
                }
            } catch (error) {
                console.error('Error shortening URL:', error);
                setShareUrl(url); // Fallback to original URL
                enqueueSnackbar(t('ShorteningError') || 'Failed to shorten URL, using original', { variant: 'warning' });
            } finally {
                setIsShortening(false);
            }
        }
        setCopied(false);
    };

    const handleClose = () => {
        setCopied(false);
        setOpen(false);
    };

    const expirationOptions = [
        { value: 24, label: t('OneDayExpiration') || '1 Day' },
        { value: 72, label: t('ThreeDaysExpiration') || '3 Days' },
        { value: 168, label: t('OneWeekExpiration') || '1 Week' }
    ];

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <Box sx={{ p: 3 }}>
                <DialogTitle>
                    <Typography variant="h4" component="div">
                        {t("ShareFile")}
                    </Typography>
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 16,
                        top: 16,
                        color: theme.palette.grey[500],
                    })}
                >
                    <CancelIcon />
                </IconButton>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t("ShareFileDesc")}
                        </Typography>

                        <FormControl fullWidth>
                            <InputLabel>{t('LinkExpiration') || 'Link Expiration'}</InputLabel>
                            <Select
                                value={expirationHours}
                                label={t('LinkExpiration') || 'Link Expiration'}
                                onChange={(e) => setExpirationHours(e.target.value)}
                                disabled={isGenerating || isShortening}
                            >
                                {expirationOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {!shareUrl && !isGenerating && (
                            <Alert severity="info">
                                {t('ClickGenerateLinkInfo') || 'Click "Generate Link" to create a shareable link.'}
                            </Alert>
                        )}

                        {(isGenerating || isShortening) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                                <CircularProgress size={24} />
                                <Typography>
                                    {isShortening 
                                        ? (t('CreatingShortLink') || 'Creating short link...') 
                                        : (t('GeneratingShareLink') || 'Generating share link...')
                                    }
                                </Typography>
                            </Box>
                        )}

                        {shareUrl && !isGenerating && !isShortening && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    {t('ShareLinkExpiresIn', { expiration: expirationOptions.find(opt => opt.value === expirationHours)?.label }) || 
                                     `Link expires in ${expirationOptions.find(opt => opt.value === expirationHours)?.label}`}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TextField
                                        fullWidth
                                        value={shareUrl}
                                        variant="outlined"
                                        size="small"
                                        slotProps={{
                                            input: {
                                                readOnly: true,
                                                style: { fontSize: '0.875rem' }
                                            }
                                        }}
                                    />
                                    <Tooltip title={copied ? (t('Copied') || 'Copied!') : (t('CopyToClipboard') || 'Copy to clipboard')}>
                                        <IconButton 
                                            onClick={handleCopyToClipboard}
                                            color={copied ? "success" : "primary"}
                                        >
                                            {copied ? <CheckIcon /> : <ContentCopyIcon />}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        <strong>{t('Important') || 'Important'}:</strong> {t('ShareLinkWarning') || 'Anyone with this link can access the shared file.'}
                                    </Typography>
                                </Alert>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    {!shareUrl && (
                        <Button 
                            onClick={handleGenerateLink} 
                            variant="contained" 
                            disabled={isGenerating || isShortening}
                            startIcon={<ShareIcon />}
                        >
                            {t('GenerateLink') || t("CreateLink")}
                        </Button>
                    )}
                    {shareUrl && (
                        <Button 
                            onClick={handleGenerateLink} 
                            variant="outlined"
                            disabled={isGenerating || isShortening}
                            startIcon={<ShareIcon />}
                        >
                            {t('GenerateNewLink') || 'Generate New Link'}
                        </Button>
                    )}
                </DialogActions>
            </Box>
        </Dialog>
    );
}